import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", message: "" };

export default function ContactDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setSubmitting(false);
      setDone(false);
    }
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please add your name.");
    if (!/.+@.+\..+/.test(form.email)) return toast.error("Please enter a valid email.");
    if (!form.message.trim()) return toast.error("Please add a quick message.");

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/contact`,
        {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          message: form.message.trim(),
        },
        { timeout: 20000 },
      );
      setDone(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.code === "ECONNABORTED"
          ? "The server took too long. Please try emailing info@nztaxation.nz directly."
          : err?.message) ||
        "Something went wrong. Please try again.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] rounded-none border-slate-200 p-0 max-h-[92vh] overflow-y-auto"
        data-testid="contact-dialog"
      >
        <div className="bg-[#0A192F] text-white px-6 py-5">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#C9A349]" /> Email the team
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              A real human reads every message. We aim to reply within one business day.
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div
            className="px-6 py-10 text-center space-y-4"
            data-testid="contact-success"
          >
            <CheckCircle2 className="w-12 h-12 text-[#C9A349] mx-auto" />
            <h3 className="font-heading font-black text-2xl tracking-tight">
              Got it — message received.
            </h3>
            <p className="text-slate-600 max-w-sm mx-auto">
              We've just sent you a confirmation email. A member of the NZ Taxation
              Limited team will get back to you shortly — usually within one business day.
            </p>
            <button
              onClick={() => onOpenChange?.(false)}
              className="mt-2 bg-[#0A192F] text-white px-6 py-3 font-medium hover:bg-[#C9A349] transition-colors"
              data-testid="contact-close"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-4" data-testid="contact-form">
            <Field label="Your name" required>
              <Input
                data-testid="contact-input-name"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349]"
                placeholder="Jane Cooper"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email" required>
                <Input
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349]"
                  placeholder="jane@business.co.nz"
                />
              </Field>
              <Field label="Phone (optional)">
                <Input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349]"
                  placeholder="021 000 0000"
                />
              </Field>
            </div>
            <Field label="How can we help?" required>
              <Textarea
                data-testid="contact-input-message"
                required
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349] min-h-[120px]"
                placeholder="Tell us a bit about your situation — sole trader / company, current accountant, what you'd like sorted…"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0A192F] text-white py-4 font-medium flex items-center justify-center gap-2 hover:bg-[#C9A349] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="contact-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send message <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="text-[11px] text-slate-500 text-center">
              We'll never share your details. Reply goes to your inbox.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
        {label}
        {required && <span className="text-[#C9A349]"> *</span>}
      </Label>
      {children}
    </div>
  );
}
